import type { Core } from '@pdftron/webviewer';
import { getIsAlineFieldName } from 'src/components/viewer/helpers';
import { pdfTronCore } from 'src/components/viewer/viewer-context';
import { Sentry } from 'src/sentry';

import { hidePdfOwnSignHereElement, lockPdfOwnInnerElement } from './lock-pdf-own-inner-element';

export function syncPageWidgetElements(
  annotationManager: Core.AnnotationManager,
  pageNum: number,
  pageWidgetContainer: HTMLElement
): boolean {
  let didAttach = false;

  for (const annotation of annotationManager.getAnnotationsList()) {
    if (!(annotation instanceof pdfTronCore.Annotations.WidgetAnnotation)) continue;
    if (annotation.PageNumber !== pageNum) continue;

    // Mid XFDF re-import a widget can exist without an element; a later draw creates one.
    const outerElement = annotation.element;
    if (!outerElement) continue;

    const isAlineAnnotation = getIsAlineFieldName(annotation.fieldName);
    const isPdfOwnSignature =
      !isAlineAnnotation && annotation instanceof pdfTronCore.Annotations.SignatureWidgetAnnotation;

    if (isPdfOwnSignature) {
      if (annotation.innerElement instanceof HTMLElement)
        hidePdfOwnSignHereElement(annotation.innerElement);
      for (const signHere of outerElement.querySelectorAll('[data-signature-field]')) {
        if (signHere instanceof HTMLElement) hidePdfOwnSignHereElement(signHere);
      }
    } else if (!annotation.innerElement) {
      // WebViewer v12 builds the container but not the inner element for read-only fields.
      const innerElement = createWidgetInnerElement(annotationManager, annotation);
      if (innerElement) {
        annotation.innerElement = innerElement;
        outerElement.appendChild(innerElement);
        annotation.refresh();
      }
    }

    // The PDF's own fields render but aren't tracked/persisted, so keep them display-only.
    if (!isAlineAnnotation) {
      outerElement.style.pointerEvents = 'none';
      if (annotation.innerElement instanceof HTMLElement)
        lockPdfOwnInnerElement(annotation.innerElement);
    }

    if (!outerElement.isConnected) {
      pageWidgetContainer.appendChild(outerElement);
      didAttach = true;
    }
  }

  return didAttach;
}

function createWidgetInnerElement(
  annotationManager: Core.AnnotationManager,
  annotation: Core.Annotations.WidgetAnnotation
): HTMLElement | null {
  try {
    const innerElement = Reflect.apply(annotation.createInnerElement, annotation, [
      annotationManager,
    ]);
    return innerElement instanceof HTMLElement ? innerElement : null;
  } catch (err) {
    Sentry.captureException(err, {
      extra: { fieldName: annotation.fieldName, widgetClass: annotation.constructor.name },
    });
    return null;
  }
}
